/**
 * Monaiq Licensing Types
 *
 * TypeScript types for the Monaiq licensing system integration.
 * Maps to Monaiq's REST API concepts: licenses, features, and authorizations.
 */

// ============================================================================
// Feature Keys — must match Monaiq product feature FeatureKey values
// ============================================================================

export const FEATURE_KEYS = {
  // Access features (Essentials tier)
  ENTITY_EXTRACTION: 'entity_extraction',
  REPORT_GENERATION: 'report_generation',

  // Access features (Professional tier)
  AI_CALLER: 'ai_caller',
  AUTO_RESPONSE: 'auto_response',
  DEEP_ANALYSIS: 'deep_analysis',
  TRAINING_MODE: 'training_mode',

  // Access features (Enterprise tier)
  KNOWLEDGE_BASE: 'knowledge_base',
  MAP_TOOL: 'map_tool',

  // Rate limit features
  API_CALLS: 'api_calls',
  CONCURRENT_CALLS: 'concurrent_calls',
  CALL_MINUTES: 'call_minutes',
} as const;

export type FeatureKey = (typeof FEATURE_KEYS)[keyof typeof FEATURE_KEYS];

// ============================================================================
// Tier definitions — for display and fallback logic
// ============================================================================

export type LicenseTier = 'essentials' | 'professional' | 'enterprise';

export const TIER_FEATURES: Record<LicenseTier, FeatureKey[]> = {
  essentials: [
    FEATURE_KEYS.ENTITY_EXTRACTION,
    FEATURE_KEYS.REPORT_GENERATION,
  ],
  professional: [
    FEATURE_KEYS.ENTITY_EXTRACTION,
    FEATURE_KEYS.REPORT_GENERATION,
    FEATURE_KEYS.AI_CALLER,
    FEATURE_KEYS.AUTO_RESPONSE,
    FEATURE_KEYS.DEEP_ANALYSIS,
    FEATURE_KEYS.TRAINING_MODE,
  ],
  enterprise: [
    FEATURE_KEYS.ENTITY_EXTRACTION,
    FEATURE_KEYS.REPORT_GENERATION,
    FEATURE_KEYS.AI_CALLER,
    FEATURE_KEYS.AUTO_RESPONSE,
    FEATURE_KEYS.DEEP_ANALYSIS,
    FEATURE_KEYS.TRAINING_MODE,
    FEATURE_KEYS.KNOWLEDGE_BASE,
    FEATURE_KEYS.MAP_TOOL,
  ],
};

export const TIER_DISPLAY: Record<LicenseTier, { name: string; label: string; description: string }> = {
  essentials: {
    name: 'Essentials',
    label: 'Essentials',
    description: 'Core conferencing, entity extraction, reporting, and file management',
  },
  professional: {
    name: 'Professional',
    label: 'Professional',
    description: 'AI caller, auto-response, deep analysis, and full training platform',
  },
  enterprise: {
    name: 'Enterprise',
    label: 'Enterprise',
    description: 'Full platform with knowledge base, map tool, and highest rate limits',
  },
};

// ============================================================================
// License authorization types (from Monaiq API responses)
// ============================================================================

export interface LicenseFeatureAccess {
  featureKey: string;
  type: 'access';
  accessLevel: 'Allowed' | 'Denied';
}

export interface LicenseFeatureRateLimit {
  featureKey: string;
  type: 'rateLimit';
  sampleSeconds: number;
  rateLimit: number;
}

export type LicenseFeature = LicenseFeatureAccess | LicenseFeatureRateLimit;

export interface LicenseAuthorization {
  /** Whether the license is valid and not expired */
  isValid: boolean;
  /** License expiry date */
  expiryDate: string | null;
  /** License classification (Trial, Subscription, Perpetual) */
  classification: string | null;
  /** Resolved tier based on enabled features */
  tier: LicenseTier | null;
  /** All licensed features */
  features: LicenseFeature[];
  /** Timestamp when this authorization was fetched */
  fetchedAt: number;
}

// ============================================================================
// Client-side licensing context
// ============================================================================

export interface LicensingState {
  /** Whether licensing data has been loaded */
  loaded: boolean;
  /** Whether the org has an active license */
  licensed: boolean;
  /** Current tier */
  tier: LicenseTier | null;
  /** Set of allowed feature keys */
  allowedFeatures: Set<string>;
  /** Rate limits by feature key */
  rateLimits: Map<string, { sampleSeconds: number; rateLimit: number }>;
  /** Error message if licensing check failed */
  error: string | null;
}

// ============================================================================
// Monaiq credential storage
// ============================================================================

export interface MonaiqCredentials {
  /** Monaiq encoded credential string (stored per-agency) */
  encodedCredential: string;
}
